// src/components/layout/LandingHeader.jsx
import React, { useContext, useState, useEffect } from "react";
import {
    AppBar,
    Box,
    Button,
    IconButton,
    Toolbar,
    Typography,
    useTheme,
    Avatar,
    Divider,
    Tooltip,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { useRouter } from "next/router";
import ThemeModeContext from "@/context/ThemeContext";
import SettingsIcon from "@mui/icons-material/Settings";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";

// Иконки для “как в дашборде”
import LoginIcon from "@mui/icons-material/Login";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonAddIcon from "@mui/icons-material/PersonAdd";


const navLinks = [
    { label: "Главная", href: "/" },
    { label: "Возможности", href: "/features" },
    { label: "Демо", href: "/demo" },
    { label: "Тарифы", href: "/pricing" },
    { label: "Контакты", href: "/contacts" },
];

export default function LandingHeader() {
    const router = useRouter();
    const theme = useTheme();
    const { mode, toggleTheme } = useContext(ThemeModeContext);
    const isDark = theme.palette.mode === "dark";

    // ───────────────────────────────────────────────────────────────────────────
    // Меню настроек (шестерёнка)
    const [anchorElSettings, setAnchorElSettings] = useState(null);
    const settingsOpen = Boolean(anchorElSettings);
    const handleSettingsClick = (event) => setAnchorElSettings(event.currentTarget);
    const handleSettingsClose = () => setAnchorElSettings(null);

    // ───────────────────────────────────────────────────────────────────────────
    // Пользователь и меню пользователя (аватар)
    const [user, setUser] = useState(null);
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const userMenuOpen = Boolean(userMenuAnchor);

    const handleUserMenuOpen = (e) => setUserMenuAnchor(e.currentTarget);
    const handleUserMenuClose = () => setUserMenuAnchor(null);

    const logout = () => {
        localStorage.removeItem("user");
        localStorage.removeItem("user_settings");
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        setUser(null);
        router.push("/");
    };

    useEffect(() => {
        const userStr = localStorage.getItem("user");
        const settingsStr = localStorage.getItem("user_settings");

        try {
            if (userStr && userStr.trim().startsWith("{")) {
                const baseUser = JSON.parse(userStr);

                if (settingsStr && settingsStr.trim().startsWith("{")) {
                    const settings = JSON.parse(settingsStr);
                    if (settings?.contact?.avatar) {
                        baseUser.avatarUrl = settings.contact.avatar;
                    }
                    if (settings?.contact?.name && !baseUser.name) {
                        baseUser.name = settings.contact.name;
                    }
                }

                setUser(baseUser);
            }
        } catch (e) {
            console.warn("Ошибка при парсинге localStorage:", e);
        }
    }, []);

    // ───────────────────────────────────────────────────────────────────────────
    // Mobile drawer
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const toggleMobileDrawer = () => setMobileDrawerOpen((v) => !v);

    // ───────────────────────────────────────────────────────────────────────────
    // “Стеклянный” стиль для всплывающих меню (полупрозрачный фон как ты просил)
    const menuPaperSx = (theme) => ({
        mt: 1,
        borderRadius: 2,
        minWidth: 220,
        bgcolor:
            theme.palette.mode === "dark"
                ? "rgba(25, 25, 25, 0.78)"
                : "rgba(255, 255, 255, 0.78)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
    });

    return (
        <AppBar
            position="sticky"
            elevation={0}
            color="default"
            sx={{
                backdropFilter: "blur(8px)",
                backgroundColor: theme.palette.background.default + "F2",
            }}
        >
            <Toolbar
                sx={{
                    maxWidth: 1400,
                    mx: "auto",
                    px: { xs: 2, md: 3 },
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                }}
            >
                {/* Логотип */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5, minHeight: "48px" }}>
                    {isMobile && (
                        <IconButton onClick={toggleMobileDrawer} sx={{ mr: 1 }}>
                            <MenuIcon />
                        </IconButton>
                    )}

                    <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1 }}>
                        <Box
                            component="span"
                            sx={{
                                background: `linear-gradient(
                  270deg,
                  #ffffff,
                  rgb(23, 135, 240),
                  rgb(245, 81, 16),
                  #ffffff
                )`,
                                backgroundSize: "400% 400%",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                animation: "gradientAnimation 6s ease infinite",
                                "@keyframes gradientAnimation": {
                                    "0%": { backgroundPosition: "0% 50%" },
                                    "50%": { backgroundPosition: "100% 50%" },
                                    "100%": { backgroundPosition: "0% 50%" },
                                },
                            }}
                        >
                            Спорт
                        </Box>
                        Подсчет
                    </Typography>
                </Box>

                {/* Правая часть */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* Навигация на десктопе */}
                    {!isMobile &&
                        navLinks.map(({ label, href }) => {
                            const isActive = router.pathname === href;
                            return (
                                <Button
                                    key={href}
                                    color="inherit"
                                    onClick={() => router.push(href)}
                                    sx={{
                                        py: 1.5,
                                        minHeight: "48px",
                                        color: isActive ? "primary.main" : "text.primary",
                                        fontWeight: isActive ? 500 : 400,
                                        display: "flex",
                                        gap: 1,
                                        textTransform: "none",
                                        alignItems: "center",
                                    }}
                                >
                                    {label}
                                </Button>
                            );
                        })}

                    {/* ⚙ Темы и внешний вид (как в дашборде + стеклянное меню) */}
                    {/* <Tooltip title="Темы и внешний вид">
                        <IconButton onClick={handleSettingsClick} color="inherit" sx={{ display: { xs: "none", sm: "block" } }}>
                            <SettingsIcon />
                        </IconButton>
                    </Tooltip> */}

                    <Menu
                        anchorEl={anchorElSettings}
                        open={settingsOpen}
                        onClose={handleSettingsClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{ sx: (theme) => menuPaperSx(theme) }}
                        MenuListProps={{ sx: { py: 0.5 } }}
                    >
                        <MenuItem disableGutters sx={{ px: 2 }}>
                            <FormControlLabel
                                control={<Switch checked={mode === "dark"} onChange={toggleTheme} />}
                                label={mode === "dark" ? "Тёмная тема" : "Светлая тема"}
                            />
                        </MenuItem>
                    </Menu>

                    {/* 👤 Аватар / Вход-Регистрация */}
                    {user ? (
                        <Tooltip title="Профиль и выход">
                            <Box
                                sx={{
                                    position: "relative",
                                    width: 48,
                                    height: 48,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {/* Вращающаяся рамка */}
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        borderRadius: "50%",
                                        padding: "1px",
                                        background:
                                            "conic-gradient(from 0deg, #ff8c00, #00d0ff, #0073ff, #366eb3, #ff2e00, #ff8c00)",
                                        animation: "spin 10s linear infinite",
                                        "@keyframes spin": {
                                            "0%": { transform: "rotate(0deg)" },
                                            "100%": { transform: "rotate(360deg)" },
                                        },
                                        zIndex: 0,
                                    }}
                                />

                                {/* Внутренний круг */}
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: "1px",
                                        borderRadius: "50%",
                                        backgroundColor: "background.paper",
                                        zIndex: 1,
                                    }}
                                />

                                {/* Аватар */}
                                <IconButton onClick={handleUserMenuOpen} sx={{ width: 42, height: 42, p: 0, zIndex: 2 }}>
                                    <Avatar
                                        alt={user.name || "Пользователь"}
                                        src={user.avatarUrl || ""}
                                        sx={{ width: 38, height: 38 }}
                                    >
                                        {!user.avatarUrl && (user.name?.[0]?.toUpperCase() || "П")}
                                    </Avatar>
                                </IconButton>

                                {/* Меню профиля (стеклянное, полупрозрачное) */}
                                <Menu
                                    anchorEl={userMenuAnchor}
                                    open={userMenuOpen}
                                    onClose={handleUserMenuClose}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                    PaperProps={{ sx: (theme) => menuPaperSx(theme) }}
                                    MenuListProps={{ sx: { py: 0.5 } }}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            router.push("/dashboard");
                                            handleUserMenuClose();
                                        }}
                                    >
                                        Перейти в сервис
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            logout();
                                            handleUserMenuClose();
                                        }}
                                    >
                                        Выйти
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Tooltip>
                    ) : (
                        <>
                            {/* ===== DESKTOP ===== */}
                            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => router.push("/login")}
                                    sx={{ textTransform: "none" }}
                                >
                                    Вход
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={() => router.push("/register")}
                                    sx={{ textTransform: "none" }}
                                >
                                    Регистрация
                                </Button>
                            </Box>

                            {/* ===== MOBILE ===== */}
                            <Box sx={{ display: { xs: "flex", md: "none" }, gap: 1 }}>
                                <IconButton
                                    onClick={() => router.push("/login")}
                                    aria-label="Вход"
                                    sx={{
                                        color: "text.primary",
                                        p: 0.5,
                                        "&:hover": {
                                            color: "primary.main",
                                            backgroundColor: "transparent",
                                        },
                                    }}
                                >
                                    <LoginIcon />
                                </IconButton>

                                <IconButton
                                    onClick={() => router.push("/register")}
                                    aria-label="Регистрация"
                                    sx={{
                                        color: "primary.main",
                                        p: 0.5,
                                        "&:hover": {
                                            color: "primary.dark",
                                            backgroundColor: "transparent",
                                        },
                                    }}
                                >
                                    <PersonAddIcon />
                                </IconButton>
                            </Box>
                        </>

                    )}
                </Box>
            </Toolbar>

            {/* Mobile Drawer (temporary) */}
            <Drawer
                anchor="left"
                variant="temporary"
                open={mobileDrawerOpen}
                onClose={toggleMobileDrawer}
                ModalProps={{ keepMounted: true }}
                sx={{
                    [`& .MuiDrawer-paper`]: {
                        width: 240,
                        boxSizing: "border-box",
                    },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1 }}>
                            <Box
                                component="span"
                                sx={{
                                    background: `linear-gradient(
                    60deg,
                    #ffffff,
                    #0073FF,
                    #FF2E00
                  )`,
                                    backgroundSize: "100% 80%",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                }}
                            >
                                Ru
                            </Box>
                            Timing
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Навигация */}
                    <List sx={{ px: 1 }}>
                        {navLinks.map(({ label, href }) => {
                            const isActive = router.pathname === href;
                            return (
                                <ListItem disablePadding key={href}>
                                    <ListItemButton
                                        selected={isActive}
                                        onClick={() => {
                                            router.push(href);
                                            setMobileDrawerOpen(false);
                                        }}
                                        sx={{
                                            borderRadius: 2,
                                            mx: 1,
                                            minHeight: 44,
                                            "&.Mui-selected": {
                                                "& .MuiListItemText-primary": { fontWeight: 700 },
                                            },
                                        }}
                                    >
                                        <ListItemText primary={label} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>

                    {/* Нижний блок (как в дашборде) */}
                    <Box sx={{ mt: "auto" }}>
                        <Divider sx={{ my: 1 }} />

                        <List sx={{ px: 1 }}>
                            {user ? (
                                <>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            selected={router.pathname === "/dashboard"}
                                            onClick={() => {
                                                router.push("/dashboard");
                                                setMobileDrawerOpen(false);
                                            }}
                                            sx={{
                                                borderRadius: 2,
                                                mx: 1,
                                                minHeight: 44,
                                                "&.Mui-selected": {
                                                    "& .MuiListItemIcon-root": { color: "primary.main" },
                                                    "& .MuiListItemText-primary": { fontWeight: "bold" },
                                                },
                                            }}
                                        >
                                            <ListItemIcon>
                                                <DashboardIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Перейти в сервис" />
                                        </ListItemButton>
                                    </ListItem>

                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => {
                                                logout();
                                                setMobileDrawerOpen(false);
                                            }}
                                            sx={{
                                                borderRadius: 2,
                                                mx: 1,
                                                minHeight: 44,
                                                color: "error.main",
                                                "& .MuiListItemIcon-root": { color: "error.main" },
                                                "&:hover": {
                                                    bgcolor: (theme) =>
                                                        theme.palette.mode === "dark"
                                                            ? "rgba(244, 67, 54, 0.12)"
                                                            : "rgba(244, 67, 54, 0.08)",
                                                },
                                            }}
                                        >
                                            <ListItemIcon>
                                                <LogoutIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Выйти" />
                                        </ListItemButton>
                                    </ListItem>
                                </>
                            ) : (
                                <>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            selected={router.pathname === "/login"}
                                            onClick={() => {
                                                router.push("/login");
                                                setMobileDrawerOpen(false);
                                            }}
                                            sx={{
                                                borderRadius: 2,
                                                mx: 1,
                                                minHeight: 44,
                                                "&.Mui-selected": {
                                                    "& .MuiListItemIcon-root": { color: "primary.main" },
                                                    "& .MuiListItemText-primary": { fontWeight: "bold" },
                                                },
                                            }}
                                        >
                                            <ListItemIcon>
                                                <LoginIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Вход" />
                                        </ListItemButton>
                                    </ListItem>

                                    <ListItem disablePadding>
                                        <ListItemButton
                                            selected={router.pathname === "/register"}
                                            onClick={() => {
                                                router.push("/register");
                                                setMobileDrawerOpen(false);
                                            }}
                                            sx={{
                                                borderRadius: 2,
                                                mx: 1,
                                                minHeight: 44,
                                                mt: 0.5,
                                                bgcolor: "primary.main",
                                                color: "primary.contrastText",
                                                "& .MuiListItemIcon-root": { color: "primary.contrastText" },
                                                "&:hover": { bgcolor: "primary.dark" },
                                                "&.Mui-selected": {
                                                    bgcolor: "primary.dark",
                                                    "&:hover": { bgcolor: "primary.dark" },
                                                    "& .MuiListItemText-primary": { fontWeight: "bold" },
                                                },
                                            }}
                                        >
                                            <ListItemIcon>
                                                <AppRegistrationIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Регистрация" />
                                        </ListItemButton>
                                    </ListItem>
                                </>
                            )}
                        </List>
                    </Box>
                </Box>
            </Drawer>
        </AppBar>
    );
}
